import {queries} from '@/shared/integrations/zero'
import {ListRestVialsInput} from '@cypro/engine/queries'
import {RestVial} from '@cypro/engine/schema'
import {Anchor, createTopAnchor} from '@cypro/ui/components/ui/infinite-data-table'
import {useQuery, useSuspenseQuery} from '@rocicorp/zero/react'
import {useEffect, useState, useMemo} from 'react'

export function useGetRestVials({
  sort = {dir: 'desc', field: 'createdAt'},
  filters,
  limit = 50,
}: Omit<Partial<ListRestVialsInput>, 'cursor'>) {
  const [anchor, setAnchor] = useState<Anchor<RestVial>>(createTopAnchor<RestVial>())

  const cursor = anchor.startRow
    ? // oxlint-disable-next-line no-non-null-assertion
      {id: anchor.startRow.id!, createdAt: anchor.startRow.createdAt!, direction: anchor.direction}
    : undefined

  const query = queries.stocks.listRestVials({
    limit,
    cursor: cursor,
    sort,
    filters,
  })

  const [restVials, meta] = useQuery(query)

  const filtersHash = useMemo(() => JSON.stringify(filters), [filters])
  useEffect(() => {
    setAnchor(createTopAnchor<RestVial>())
  }, [sort?.dir, sort?.field, filtersHash, limit])

  return {
    anchor,
    fetchMore: setAnchor,
    query,
    restVials,
    meta,
    canFetchMore: restVials.length === limit,
    isFetching: meta.type === 'unknown',
  }
}

export function useSuspenseGetRestVials({
  sort = {dir: 'desc', field: 'createdAt'},
  filters,
  limit = 50,
}: Omit<Partial<ListRestVialsInput>, 'cursor'>) {
  const [anchor, setAnchor] = useState<Anchor<RestVial>>(createTopAnchor<RestVial>())

  const cursor = anchor.startRow
    ? // oxlint-disable-next-line no-non-null-assertion
      {id: anchor.startRow.id!, createdAt: anchor.startRow.createdAt!, direction: anchor.direction}
    : undefined

  const query = queries.stocks.listRestVials({
    limit,
    cursor: cursor,
    sort,
    filters,
  })

  // When selecting rest vials by id and none are provided, there is nothing to
  // load — disable the query so it neither suspends forever nor fetches every
  // rest vial in the system (empty `filters.ids` otherwise drops the id filter).
  const ids = filters?.ids ?? []
  const hasIds = ids.length > 0

  const [restVials, meta] = useSuspenseQuery(query, {enabled: hasIds})
  const normalizedRestVials = hasIds ? restVials : []

  const filtersHash = useMemo(() => JSON.stringify(filters), [filters])
  useEffect(() => {
    setAnchor(createTopAnchor<RestVial>())
  }, [sort?.dir, sort?.field, filtersHash, limit])

  return {
    anchor,
    fetchMore: setAnchor,
    query,
    restVials: normalizedRestVials,
    meta,
    canFetchMore: normalizedRestVials.length === limit,
    isFetching: meta.type === 'unknown',
  }
}
